import React, { useState, useEffect, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { fadeUp, fadeUpStagger, viewport } from '../utils/animations';

/* ─── Optimized Lazy Video Component ────────────────────── */
const LazyVideo = memo(function LazyVideo({ src, autoPlay, className, ...props }) {
  const videoRef = useRef(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!hasLoaded) setHasLoaded(true);
          if (autoPlay && videoRef.current) {
            videoRef.current.play().catch(() => {});
          }
        } else {
          if (autoPlay && videoRef.current) {
            videoRef.current.pause();
          }
        }
      },
      { rootMargin: '600px' }
    );
    const el = videoRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [autoPlay, hasLoaded]);

  return (
    <video
      ref={videoRef}
      src={hasLoaded ? src : undefined}
      className={className}
      autoPlay={autoPlay && hasLoaded}
      {...props}
    />
  );
});

const servicesMatrix = [
  {
    id: 1,
    title: 'INTERVIEWS',
    alt: 'Executive interview video production featuring Jiquan Ngiam, Founder of MintMCP — Bay Area Podcast Studio',
    video: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/05c20469eb9d7900bf8404da1dd40aa8.mov',
    image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=1600&q=80',
    client: { name: 'Jiquan Ngiam', title: 'Founder, MintMCP' },
    dualClient: null,
  },
  {
    id: 2,
    title: 'VIDEO SALES LETTERS',
    alt: 'Video sales letter production for Nathaniel Rutkoski, Owner of Zoom Loans — Bay Area Podcast Studio',
    video: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/520b7dd497bd8f17457763457032b939.mov',
    image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=1600&q=80',
    client: { name: 'Nathaniel Rutkoski', title: 'Owner, Zoom Loans' },
    dualClient: null,
  },
  {
    id: 3,
    title: 'CUSTOMER TESTIMONIALS',
    alt: 'Customer testimonial video production featuring Donna Ruff, President of WRMSDC — Bay Area Podcast Studio',
    video: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/74f03d2c3c81e396dcb48a355c71547e.mov',
    image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1600&q=80',
    client: { name: 'Donna Ruff', title: 'President, WRMSDC' },
    dualClient: null,
  },
  {
    id: 4,
    title: 'FIRESIDE CHATS',
    alt: 'Fireside chat video production — WRMSDC Annual Business Summit & Awards Luncheon — Bay Area Podcast Studio',
    video: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/8c00a8d10dbe5a0d000cde7bb39ddf78.mov',
    image: 'https://images.unsplash.com/photo-1542204165-65bf26472b9b?auto=format&fit=crop&w=1600&q=80',
    client: { name: 'WRMSDC Annual Business Summit', title: 'Awards Luncheon' },
    dualClient: null,
  },
  {
    id: 5,
    title: 'VIDEO PODCASTING',
    alt: 'Video podcast production featuring Amar Amte, Founder of Pegbo and Nataliya Scarberry, Founder of Contenu — Bay Area Podcast Studio Fremont CA',
    video: 'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/013d50379da729be40204f78079a0a1d.mov',
    image: 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?auto=format&fit=crop&w=1600&q=80',
    client: null,
    dualClient: {
      left:  { name: 'Amar Amte',         title: 'Founder, Pegbo'   },
      right: { name: 'Nataliya Scarberry', title: 'Founder, Contenu' },
    },
  },
];

const DEFAULT_META = {
  description: 'Premier turnkey video podcast studio in Fremont, CA. Serving San Francisco and Silicon Valley executive teams, founders, and creators with 4K multi-cam studio sessions and on-location video production.',
  ogTitle: 'Bay Area Podcast Studio | Premium Video Podcast & Content Studio',
  ogDescription: 'Turnkey 4K video podcasting and commercial production studio in Fremont, CA.',
  ogUrl: 'https://bayareapodcast.studio/',
  twTitle: 'Bay Area Podcast Studio | Premium Video Podcast & Content Studio',
  twDesc: 'Turnkey 4K video podcasting and commercial production studio in Fremont, CA.',
};

const BREADCRUMB_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home',           item: 'https://bayareapodcast.studio/' },
    { '@type': 'ListItem', position: 2, name: 'Video Services', item: 'https://bayareapodcast.studio/video-services' },
  ],
};

function setMetaTag(selector, attr, value) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [attrName, attrVal] = selector.replace('meta[', '').replace(']', '').split('="');
    el.setAttribute(attrName, attrVal.replace('"', ''));
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function DualClientOverlay({ dualClient }) {
  return (
    <motion.div
      className="absolute bottom-0 left-0 right-0 z-10 flex justify-between items-end px-4 pb-4 pt-16"
      style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0) 100%)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="text-left">
        <p className="text-white text-sm font-semibold tracking-tight leading-snug">{dualClient.left.name}</p>
        <p className="text-neutral-300 text-xs font-medium tracking-widest uppercase mt-0.5">{dualClient.left.title}</p>
      </div>
      <div className="text-right">
        <p className="text-white text-sm font-semibold tracking-tight leading-snug">{dualClient.right.name}</p>
        <p className="text-neutral-300 text-xs font-medium tracking-widest uppercase mt-0.5">{dualClient.right.title}</p>
      </div>
    </motion.div>
  );
}

/* ─── FormRobin Embed Component ────────────────────── */
const FormRobinEmbed = memo(function FormRobinEmbed() {
  return (
    <div className="w-full relative rounded-md overflow-hidden bg-transparent scrollbar-hide">
      <iframe
        src="https://formrobin.com/f/w05kklj"
        width="100%"
        height="850"
        frameBorder="0"
        style={{ border: 'none', backgroundColor: 'transparent', minHeight: '850px' }}
        title="Project Inquiry Form"
        allow="microphone; camera"
      />
    </div>
  );
});

export default function VideoServices() {
  const [activeService, setActiveService] = useState(servicesMatrix[0]);

  // ── title ────────────────────────────────────────────────────────────────
  useEffect(() => {
    document.title = 'Video Production Services | Bay Area Podcast Studio';
    return () => {
      document.title = 'Bay Area Podcast Studio | Premium Video Podcast & Content Studio';
    };
  }, []);

  // ── meta tags (with full cleanup) ───────────────────────────────────────
  useEffect(() => {
    const prev = {
      desc:    document.querySelector('meta[name="description"]')?.getAttribute('content')        || DEFAULT_META.description,
      ogTitle: document.querySelector('meta[property="og:title"]')?.getAttribute('content')       || DEFAULT_META.ogTitle,
      ogDesc:  document.querySelector('meta[property="og:description"]')?.getAttribute('content') || DEFAULT_META.ogDescription,
      ogUrl:   document.querySelector('meta[property="og:url"]')?.getAttribute('content')         || DEFAULT_META.ogUrl,
      twTitle: document.querySelector('meta[name="twitter:title"]')?.getAttribute('content')      || DEFAULT_META.twTitle,
      twDesc:  document.querySelector('meta[name="twitter:description"]')?.getAttribute('content')|| DEFAULT_META.twDesc,
    };

    setMetaTag('meta[name="description"]',        'content', 'High-end video production in the San Francisco Bay Area. Executive interviews, customer testimonials, video sales letters, fireside chats, and video podcasting. Featured clients include Jiquan Ngiam of MintMCP, Nathaniel Rutkoski of Zoom Loans, Donna Ruff of WRMSDC, the WRMSDC Annual Business Summit & Awards Luncheon, Amar Amte of Pegbo, and Nataliya Scarberry of Contenu.');
    setMetaTag('meta[name="keywords"]',           'content', 'video production Bay Area, Jiquan Ngiam MintMCP, MintMCP founder interview, Nathaniel Rutkoski Zoom Loans, Donna Ruff WRMSDC, WRMSDC Annual Business Summit, WRMSDC Awards Luncheon, WRMSDC event video production, Amar Amte Pegbo, Pegbo founder video, Nataliya Scarberry Contenu, Contenu founder video, video podcast Bay Area founders, video sales letter production Bay Area, customer testimonial video production, fireside chat video production, executive interview video Bay Area, founder interview video Silicon Valley, corporate video San Francisco, video podcast studio Fremont CA, Silicon Valley video production');
    setMetaTag('meta[property="og:title"]',       'content', 'Video Production Services | Bay Area Podcast Studio');
    setMetaTag('meta[property="og:description"]', 'content', 'Corporate and commercial video production across Silicon Valley and the Bay Area. Featuring work with Jiquan Ngiam of MintMCP, Nathaniel Rutkoski of Zoom Loans, Donna Ruff of WRMSDC, the WRMSDC Annual Business Summit & Awards Luncheon, Amar Amte of Pegbo, and Nataliya Scarberry of Contenu.');
    setMetaTag('meta[property="og:url"]',         'content', 'https://bayareapodcast.studio/video-services');
    setMetaTag('meta[name="twitter:title"]',      'content', 'Video Production Services | Bay Area Podcast Studio');
    setMetaTag('meta[name="twitter:description"]','content', 'Corporate and commercial video production across Silicon Valley and the Bay Area.');

    return () => {
      setMetaTag('meta[name="description"]',        'content', prev.desc);
      setMetaTag('meta[property="og:title"]',       'content', prev.ogTitle);
      setMetaTag('meta[property="og:description"]', 'content', prev.ogDesc);
      setMetaTag('meta[property="og:url"]',         'content', prev.ogUrl);
      setMetaTag('meta[name="twitter:title"]',      'content', prev.twTitle);
      setMetaTag('meta[name="twitter:description"]','content', prev.twDesc);
    };
  }, []);

  // ── BreadcrumbList JSON-LD ───────────────────────────────────────────────
  useEffect(() => {
    const id = 'ld-videoservices-breadcrumb';
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(BREADCRUMB_JSON_LD);
    return () => {
      const tag = document.getElementById(id);
      if (tag) tag.remove();
    };
  }, []);

  return (
    <main className="bg-[#000000] text-[#FFFFFF] overflow-x-hidden scrollbar-hide">

      {/* SECTION 1 — CINEMATIC HERO */}
      <section className="w-full min-h-screen flex flex-col items-center justify-center pt-24 md:pt-32 pb-12 md:pb-16 px-5 md:px-12 overflow-hidden">
        <motion.div
          className="w-full max-w-5xl mx-auto text-center mb-10 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white tracking-tight uppercase text-center mb-5 leading-tight">
            YOUR STORY.<br className="sm:hidden" /> OUR CAMERAS.
          </h1>
          <motion.p
            className="text-base md:text-xl font-medium text-neutral-200 text-center max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            End-to-end video production for forward-thinking brands.
          </motion.p>
        </motion.div>
        <motion.div
          className="w-full max-w-7xl mx-auto overflow-hidden shadow-2xl shadow-white/5 border border-white/10 aspect-video md:aspect-[21/9]"
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/6e3aee9d89c4953d6dea405ed8f732bd.png"
            alt="On-location video production setup with cinema cameras and lighting in the Bay Area"
            className="w-full h-full object-cover object-center"
          />
        </motion.div>
      </section>

      {/* SECTION 2 — SERVICES MATRIX */}
      <section className="w-full py-16 md:py-32 px-5 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 relative">

            {/* LEFT — service list */}
            <motion.div
              className="flex flex-col justify-center"
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              variants={fadeUpStagger}
            >
              {servicesMatrix.map((service) => (
                <motion.div
                  key={service.id}
                  variants={fadeUp}
                  onMouseEnter={() => setActiveService(service)}
                  onTouchStart={() => setActiveService(service)}
                  onClick={() => setActiveService(service)}
                  className="py-5 md:py-6 border-b border-neutral-800 cursor-pointer transition-colors duration-300 group"
                >
                  <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight transition-colors duration-300 leading-tight ${activeService.id === service.id ? 'text-white' : 'text-neutral-600 group-hover:text-white'}`}>
                    {service.title}
                  </h2>
                </motion.div>
              ))}
            </motion.div>

            {/* RIGHT — video preview */}
            <motion.div
              className="relative w-full"
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              variants={fadeUp}
            >
              <div className="md:sticky md:top-32 w-full">
                <div className="w-full aspect-video bg-black border border-neutral-800 overflow-hidden relative scrollbar-hide">

                  <AnimatePresence mode="wait">
                    {activeService.video ? (
                      <motion.div
                        key={`video-wrapper-${activeService.id}`}
                        initial={{ opacity: 0, scale: 1.02, filter: 'brightness(1.5)' }}
                        animate={{ opacity: 1, scale: 1, filter: 'brightness(1)' }}
                        exit={{ opacity: 0, scale: 1.02, filter: 'brightness(0)' }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="absolute inset-0 w-full h-full"
                      >
                        <LazyVideo
                          src={activeService.video}
                          aria-label={activeService.alt}
                          autoPlay loop muted playsInline
                          className="w-full h-full object-cover object-center absolute inset-0"
                        />
                      </motion.div>
                    ) : (
                      <motion.img
                        key={`img-${activeService.id}`}
                        src={activeService.image}
                        alt={activeService.alt}
                        initial={{ opacity: 0, scale: 1.02, filter: 'brightness(1.5)' }}
                        animate={{ opacity: 1, scale: 1, filter: 'brightness(1)' }}
                        exit={{ opacity: 0, scale: 1.02, filter: 'brightness(0)' }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="w-full h-full object-cover object-center absolute inset-0"
                      />
                    )}
                  </AnimatePresence>

                  {/* SINGLE CLIENT ATTRIBUTION */}
                  <AnimatePresence>
                    {activeService.client && (
                      <motion.div
                        key={`client-${activeService.id}`}
                        className="absolute bottom-0 left-0 right-0 z-10 px-4 pb-4 pt-12"
                        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0) 100%)' }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <p className="text-white text-sm font-semibold tracking-tight leading-snug">
                          {activeService.client.name}
                        </p>
                        <p className="text-neutral-300 text-xs font-medium tracking-widest uppercase mt-0.5">
                          {activeService.client.title}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* DUAL CLIENT ATTRIBUTION */}
                  <AnimatePresence>
                    {activeService.dualClient && (
                      <DualClientOverlay
                        key={`dual-${activeService.id}`}
                        dualClient={activeService.dualClient}
                      />
                    )}
                  </AnimatePresence>

                </div>
              </div>
            </motion.div>

          </div>

          {/* CTA */}
          <motion.div
            className="w-full text-center mt-16 md:mt-24"
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={fadeUp}
          >
            <Link
              to="/portfolio"
              className="inline-block w-full sm:w-auto px-8 md:px-12 py-4 md:py-6 bg-white text-black text-sm md:text-lg font-bold uppercase tracking-widest hover:bg-neutral-200 transition-colors"
            >
              VIEW FULL PORTFOLIO
            </Link>
          </motion.div>
        </div>
      </section>

      {/* SECTION 3 — INQUIRY FORM */}
      <motion.section
        className="w-full pt-16 md:pt-24 pb-16 md:pb-24 px-5 md:px-12"
        id="contact"
        initial="hidden"
        whileInView="visible"
        viewport={viewport}
        variants={fadeUp}
      >
        <div className="max-w-3xl mx-auto">
          <div className="border-t border-neutral-900 w-full mb-12 md:mb-16"></div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black uppercase tracking-tight text-center text-white mb-8 md:mb-12">
            TELL US WHAT YOU'RE WORKING ON.
          </h2>
          <FormRobinEmbed />
        </div>
      </motion.section>

    </main>
  );
}